import Select, { ControlProps, CSSObjectWithLabel, Props as SelectProps } from 'react-select';
import { ConnectForm } from '../form/form';
import React, { FC, ReactElement } from 'react';
import cx from 'classnames';

type Option = { label: string; value: any };

interface Props extends SelectProps {
    label?: string;
    name: string;
    options: Option[];
    isMulti?: boolean;
    isSearchable?: boolean;
    inlineLabel?: boolean;
    onChange?: (newValue: unknown) => void;
    maxWidth?: string;
    layout?: 'normal' | 'horizontal';
}

const FormSelect: FC<Props> = (props) => {
    const {
        name,
        id,
        label,
        options,
        isMulti = false,
        inlineLabel = true,
        maxWidth,
        onChange,
        isLoading,
        layout = 'normal',
        ...rest
    } = props;

    return (
        <ConnectForm>
            {(methods: any) => {
                const { register, formState, setValue, watch, isRequired } =
                    methods;
                const fieldError = formState?.errors?.[name];
                const inputId: string = id || name;
                const currentSelectedValue = watch(name);

                const errorMessage: ReactElement = (
                    <>
                        { layout === 'horizontal' ?
                            <strong className="text-brand_status-error w-full">
                                {fieldError?.message}
                            </strong> :
                            <strong className="text-brand_status-error w-full">
                                {fieldError?.message}
                            </strong>
                        }
                    </>
                );


                
                const getCurrentMultiSelectedOptions = () => options.filter(
                    ({ value }: Option) => (currentSelectedValue || []).includes(value)        
                );
               
                const handleMultiSelectChange = (options: Option[]) => {
                    onChange?.(options);
                    const values = options.map(option => option.value);
                    setValue(name, values, { shouldDirty: true });
                };
            
                const getCurrentSingleSelectedOption = () => options.filter(
                    // eslint-disable-next-line eqeqeq
                    ({ value }: Option) => value == currentSelectedValue
                );
            
                const handleSingleSelectChange = (option: Option) => {
                    onChange?.(option);
                    setValue(name, option?.value, { shouldDirty: true });
                };

                const selectInputStyles = {
                    control: (baseStyles: CSSObjectWithLabel) => ({
                        ...baseStyles,
                        boxShadow: 'none',
                    }),
                };

                const selectInputClassNames = {
                    control: ({ isFocused }: ControlProps) =>
                        cx('border', {
                            'border-info': isFocused && !fieldError,
                            'border-danger': !!fieldError,
                        }),
                };

                return (
                    <>
                        { layout === 'horizontal' ?
                            <div className="flex flex-col gap-1 lg:flex-row lg:gap-2 lg:items-center lg:flex-wrap">
                                {label && (
                                    <label
                                        htmlFor={inputId}
                                        className="font-medium lg:min-w-[400px] lg:max-w-[400px] lg:text-lg"
                                    >
                                        {label}
                                        {isRequired(name) && ' *'}
                                    </label>
                                )}
                                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-6 lg:gap-10 flex-grow">
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-x-5 w-full max-w-[600px]">
                                        <div className={cx('form-component form-select flex-grow', { 'form-error' : fieldError })} style={{maxWidth: maxWidth}}>
                                        <Select
                                            {...register(name)}
                                            {...rest}
                                            styles={selectInputStyles}
                                            classNames={selectInputClassNames}
                                            inputId={inputId}
                                            options={options}
                                            value={isMulti ? getCurrentMultiSelectedOptions() : getCurrentSingleSelectedOption() }
                                            onChange={isMulti ? handleMultiSelectChange : handleSingleSelectChange}
                                            isMulti={isMulti}
                                            isLoading={isLoading}
                                        />                                    
                                        </div>
                                    </div>
                                    {fieldError && errorMessage}
                                </div>
                            </div> :   
                            <div className={cx('form-component form-select w-full', { 'form-inline' : inlineLabel, 'form-error' : fieldError })} style={{maxWidth: maxWidth}}>
                                {label && (
                                    <label
                                        htmlFor={inputId}
                                    >
                                        {label}
                                        {isRequired(name) && ' *'}
                                    </label>
                                )}
                                <Select
                                {...register(name)}
                                    {...rest}
                                    styles={selectInputStyles}
                                    classNamePrefix="select"
                                    classNames={selectInputClassNames}
                                    inputId={inputId}
                                    options={options}
                                    value={isMulti ? getCurrentMultiSelectedOptions() : getCurrentSingleSelectedOption() }
                                    onChange={isMulti ? handleMultiSelectChange : handleSingleSelectChange}
                                    unstyled
                                    isMulti={isMulti}
                                    isLoading={isLoading}
                                />                                  
                                {fieldError && errorMessage}
                            </div>
                        }
                    </>

                );
            }}
        </ConnectForm>
    );
};

export { FormSelect };